// Homepage only: each #MainContent section is a full-screen slide, one wheel/key gesture = one slide.
// Native document scroll is kept (header sticky + observers rely on it), we only drive the gesture.

const DURATION = 600; // ms, same as Byredo
const GESTURE_GAP = 300; // ms without wheel event before a new gesture is accepted
const ACCELERATION = 1.5; // mean delta jump (last WINDOW vs the WINDOW before) that counts as a new trackpad swipe
const WINDOW = 4; // wheel events per averaging window
const SETTLE_DELAY = 150; // ms of scroll silence before re-aligning on the nearest slide
const MQ = matchMedia(
  '(min-width: 750px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)'
);

const header = document.querySelector('header-component');
const initialTransparent = header?.getAttribute('transparent');
const slides = () => [...document.querySelectorAll('#MainContent > .shopify-section')];

let animating = false;
let lastWheel = 0;
let lastDir = 0;
let recentDeltas = [];
let tailPeak = 0; // highest delta of the current gesture that already left the averaging windows
let gestureStepped = false; // the current wheel gesture already triggered a step: swallow its inertia tail
let toneRaf = null;

// CSS `ease` = cubic-bezier(0.25, 0.1, 0.25, 1), the curve Byredo's Swiper uses (600ms).
// Solve x(s) = t by bisection, return y(s).
const ease = (t) => {
  const bezier = (s, p1, p2) => 3 * (1 - s) ** 2 * s * p1 + 3 * (1 - s) * s ** 2 * p2 + s ** 3;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (bezier(mid, 0.25, 0.25) < t) lo = mid;
    else hi = mid;
  }
  return bezier((lo + hi) / 2, 0.1, 1);
};

function scrollToY(target) {
  const start = scrollY;
  const t0 = performance.now();
  animating = true;
  const step = (now) => {
    const t = Math.min((now - t0) / DURATION, 1);
    window.scrollTo({ top: start + (target - start) * ease(t), behavior: 'instant' });
    if (t < 1) requestAnimationFrame(step);
    else animating = false;
  };
  requestAnimationFrame(step);
}

/** @returns {{ top: number, bottom: number, tall: boolean }[]} */
function geometry() {
  const g = slides().map((el, i) => {
    const rect = el.getBoundingClientRect();
    const top = i === 0 ? 0 : rect.top + scrollY; // first stop includes the announcement bar
    const bottom = rect.bottom + scrollY;
    return { top, bottom, tall: bottom - top > innerHeight + 2 };
  });

  // The footer is one last stop: slide up until it is fully shown (or its top reaches the viewport top if taller).
  const last = g.at(-1);
  const pageBottom = document.documentElement.scrollHeight;
  const footerStop = last && Math.min(pageBottom - innerHeight, last.bottom);
  if (last && footerStop > last.top + 2) {
    g.push({ top: footerStop, bottom: pageBottom, tall: pageBottom - footerStop > innerHeight + 2 });
  }
  return g;
}

/**
 * Where a gesture in `dir` should go: a scroll target, or null to let the browser scroll natively.
 * @param {1 | -1} dir
 */
function targetFor(dir) {
  const g = geometry();
  if (!g.length) return null;
  const y = scrollY;
  let i = g.findLastIndex((s) => s.top <= y + 2);
  if (i < 0) i = 0;
  const s = g[i];

  if (dir > 0) {
    if (s.tall && s.bottom > y + innerHeight + 2) return null; // finish the tall slide natively
    if (i === g.length - 1) return null; // past the footer stop: nothing left to slide
    return g[i + 1].top;
  }

  if (y > s.top + 2) return s.tall ? null : s.top; // mid-slide: back to its top
  if (i === 0) return null;
  const prev = g[i - 1];
  return prev.tall ? prev.bottom - innerHeight : prev.top;
}

/** @param {WheelEvent} event */
function onWheel(event) {
  if (event.ctrlKey || Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;
  // A gesture ends on a pause or a direction change, so a burst of mouse notches = one slide.
  // A trackpad inertia tail keeps firing deltas for 1-2s: a new swipe during it shows up as the mean of
  // the last WINDOW deltas clearly above the WINDOW before, once the tail has decayed to under half of
  // the gesture's early peak. Averaging ignores the frame jitter and the momentum hand-off at finger lift;
  // the decay condition ignores a mouse wheel accelerating.
  const now = performance.now();
  const abs = Math.abs(event.deltaY);
  const dir = Math.sign(event.deltaY);
  recentDeltas.push(abs);
  if (recentDeltas.length > WINDOW * 2) tailPeak = Math.max(tailPeak, recentDeltas.shift());
  const mean = (a) => a.reduce((sum, d) => sum + d, 0) / a.length;
  const older = mean(recentDeltas.slice(0, WINDOW));
  const newer = mean(recentDeltas.slice(WINDOW));
  const isNewGesture =
    now - lastWheel > GESTURE_GAP ||
    dir !== lastDir ||
    (recentDeltas.length === WINDOW * 2 && newer >= 6 && newer > older * ACCELERATION && older < tailPeak / 2);
  lastWheel = now;
  lastDir = dir;
  if (isNewGesture) {
    recentDeltas = [abs];
    tailPeak = 0;
    gestureStepped = false;
  }

  if (animating || gestureStepped) return event.preventDefault();
  const target = targetFor(event.deltaY > 0 ? 1 : -1);
  if (target === null) return;
  event.preventDefault();
  if (!isNewGesture) return;
  gestureStepped = true;
  scrollToY(target);
}

const KEYS = { ArrowDown: 1, PageDown: 1, ' ': 1, ArrowUp: -1, PageUp: -1 };

/** @param {KeyboardEvent} event */
function onKey(event) {
  const dir = KEYS[event.key];
  if (!dir || event.altKey || event.ctrlKey || event.metaKey) return;
  if (event.key === ' ' && event.shiftKey) return;
  const el = document.activeElement;
  if (el instanceof HTMLElement && (el.isContentEditable || el.closest('input, textarea, select, button, [role="slider"]'))) return;
  if (animating) return event.preventDefault();
  const target = targetFor(dir);
  if (target === null) return;
  event.preventDefault();
  scrollToY(target);
}

// Header veil: transparent over heroes, black behind it over light sections.
function updateTone() {
  toneRaf = null;
  if (!header) return;
  const line = header.getBoundingClientRect().bottom / 2;
  const current = slides().find((el) => el.getBoundingClientRect().bottom > line);
  // ponytail: hero = dark, anything else = light; switch to background luminance if a dark non-hero section shows up
  header.dataset.tone = !current || current.querySelector('.hero') ? 'dark' : 'light';
}

// Safety net: whatever left the page between two slides (inertia, scrollbar, focus), re-align once it settles.
function settle() {
  if (animating) return;
  const g = geometry();
  const y = scrollY;
  if (!g.length || y > g.at(-1).top) return; // below the footer stop scrolls freely
  const stops = g.flatMap((s) => (s.tall ? [s.top, s.bottom - innerHeight] : [s.top]));
  const inTall = g.some((s) => s.tall && y >= s.top - 2 && y <= s.bottom - innerHeight + 2);
  if (inTall || stops.some((stop) => Math.abs(stop - y) <= 2)) return;
  scrollToY(stops.reduce((a, b) => (Math.abs(b - y) < Math.abs(a - y) ? b : a)));
}

let settleTimer;
const onScroll = () => {
  toneRaf ??= requestAnimationFrame(updateTone);
  clearTimeout(settleTimer);
  settleTimer = setTimeout(settle, SETTLE_DELAY);
};

function toggle() {
  const on = MQ.matches;
  const method = on ? 'addEventListener' : 'removeEventListener';
  window[method]('wheel', onWheel, { passive: false });
  window[method]('keydown', onKey);
  window[method]('scroll', onScroll, { passive: true });

  if (!header) return;
  if (on) {
    header.setAttribute('transparent', 'always');
    updateTone();
  } else {
    if (initialTransparent) header.setAttribute('transparent', initialTransparent);
    delete header.dataset.tone;
  }
}

MQ.addEventListener('change', toggle);
toggle();
